import { Alert, Box, CircularProgress, Container, Stack, Typography, useTheme } from '@mui/material'
import { ContainedButton, OutlinedButton } from '../../../components/Button'
import { useUpload } from '../../../hooks/upload'
import { OptionCard, UploadDropzone, UPLOAD_OPTIONS } from './subComponents'
import { useVerificationGuard } from '../../../hooks/auth/useVerificationGuard'
import { useState, useRef, useCallback, useEffect } from 'react'
import { TestStripOverlay } from './components/TestStripOverlay'
import { CameraInstructionsDialog } from './components/CameraInstructionsDialog'
import Webcam from 'react-webcam'
import * as tf from '@tensorflow/tfjs'
import { detectStrip } from '../../../utils/stripDetection'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'
import { useNavigate } from 'react-router'

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

export const Upload = () => {
  const theme = useTheme()
  const isVerified = useVerificationGuard();
  const { showError } = useSnackbar()

  const {
    currentState,
    setCurrentState,
    capturedImage,
    isAnalyzing,
    handleAnalyze,
    handleImageDrop,
    resetUpload,
  } = useUpload()

  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const [isStripPositioned, setIsStripPositioned] = useState(false);
  const [guidanceMessage, setGuidanceMessage] = useState<string>('');
  const navigate = useNavigate();

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: /Mobi|Android|iPhone/i.test(navigator.userAgent) 
            ? 'environment'  // Use back camera on mobile
            : 'user'        // Use webcam on desktop
        } 
      });
      setStream(mediaStream);
      setCurrentState('camera');
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      showError('Unable to access camera. Please check permissions.');
    }
  };

  const takePhoto = () => {
    if (webcamRef.current) {
      const video = webcamRef.current.video;
      if (!video) return;

      // Create a canvas with proper aspect ratio
      const canvas = document.createElement('canvas');
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      // Calculate silhouette dimensions - exactly matching detection
      const silhouette = {
        x: Math.floor(videoWidth * 0.45),
        y: Math.floor(videoHeight * 0.15),
        width: Math.floor(videoWidth * 0.10),
        height: Math.floor(videoHeight * 0.70)
      };

      // Set canvas size to match vertical orientation
      canvas.width = silhouette.width;
      canvas.height = silhouette.height;

      // Get the context and draw the cropped region
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear the canvas with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image maintaining vertical orientation
        ctx.drawImage(
          video,
          silhouette.x,
          silhouette.y,
          silhouette.width,
          silhouette.height,
          0,
          0,
          canvas.width,
          canvas.height
        );

        // Convert to data URL with high quality
        const imageSrc = canvas.toDataURL('image/jpeg', 0.8);
        if (imageSrc) {
          const file = dataURLtoFile(imageSrc, 'camera-photo.jpg');
          if (file) {
            handleImageDropWithSizeCheck([file]);
            stopCamera();
          }
        }
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setCurrentState('upload');
    }
  };

  const dataURLtoFile = (dataurl: string, filename: string): File | null => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const file = new File([u8arr], filename, { type: mime });
    
    if (file.size > MAX_FILE_SIZE) {
      showError('Image size must be less than 10MB. Please try capturing again from a bit further away.');
      return null;
    }
    return file;
  };

  const handleImageDropWithSizeCheck = useCallback((files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    if (file.size > MAX_FILE_SIZE) {
      showError('Image size must be less than 10MB. Please choose a smaller image.');
      return;
    }

    handleImageDrop(files);
  }, [showError, handleImageDrop]);

  // Initialize TensorFlow.js
  useEffect(() => {
    const initTF = async () => {
      try {
        await tf.ready();
        if (!tf.getBackend()) {
          await tf.setBackend('webgl');
        }
      } catch (error) {
        console.error('Error initializing TensorFlow:', error);
      }
    };
    initTF();
  }, []);

  const checkStripPosition = useCallback(async () => {
    if (!webcamRef.current) return;
    
    const video = webcamRef.current.video;
    if (!video) return;

    try {
      // Capture frame from webcam
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      const canvas = document.createElement('canvas');
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
      
      // Convert to tensor for analysis
      const imageData = ctx.getImageData(0, 0, videoWidth, videoHeight);
      const tensor = tf.browser.fromPixels(imageData);
      
      try {
        const stripDetected = await detectStrip(tensor);
        if (stripDetected.found) {
          setIsStripPositioned(true);
          setGuidanceMessage(stripDetected.guidance);
        } else {
          setIsStripPositioned(false);
          setGuidanceMessage(stripDetected.guidance);
        }
      } catch (error) {
        console.error('Error in strip detection:', error);
        setIsStripPositioned(false);
        setGuidanceMessage('Error analyzing image - please try again');
      }
    } catch (error) {
      console.error('Error in checkStripPosition:', error);
      setIsStripPositioned(false);
      setGuidanceMessage('Error accessing camera - please check permissions');
    }
  }, [webcamRef]);

  useEffect(() => {
    if (currentState === 'camera') {
      const interval = setInterval(checkStripPosition, 500);
      return () => clearInterval(interval);
    }
  }, [currentState, checkStripPosition]);

  const renderContent = () => {
    switch (currentState) {
      case 'options':
        return (
          <Stack direction={'column'} spacing={3}>
            <Stack direction={'column'} spacing={3}>
              <Stack direction={{xs: "column", md:"row"}} spacing={3}>
                {UPLOAD_OPTIONS.slice(0, 2).map((option, index) => (
                  <OptionCard
                    key={index}
                    icon={option.icon}
                    title={option.title}
                    description={option.description}
                    onClick={() => {
                      if (option.action === 'upload') {
                        setCurrentState('upload');
                      } else if (option.action === 'camera') {
                        setShowInstructions(true);
                      }
                    }}
                    sx={{ width: { xs: '100%', md: '50%' } }}
                  />
                ))}
              </Stack>
              {UPLOAD_OPTIONS.slice(2).map((option, index) => (
                <OptionCard
                  key={index + 2}
                  icon={option.icon}
                  title={option.title}
                  description={option.description}
                  onClick={() => {
                    if (option.action === 'upload') {
                      setCurrentState('upload');
                    } else if (option.action === 'camera') {
                      setShowInstructions(true);
                    }else if (option.action === 'manual') {
                      navigate('/test-strip/upload-manual');
                    }
                  }}
                  sx={{ width: '100%' }}
                />
              ))}
            </Stack>
            <Alert severity="info">Place strip on a white background in good lighting</Alert>
          </Stack>
        )

      case 'camera':
        return (
          <Box 
            sx={{ 
              width: '100%',
              maxWidth: '500px',
              height: 'calc(100vh - 100px)',
              position: 'relative',
              backgroundColor: '#000',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
              overflow: 'hidden',
              mx: 'auto'
            }}
          >
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 720,
                height: 1280,
                facingMode: /Mobi|Android|iPhone/i.test(navigator.userAgent)
                  ? 'environment'
                  : 'user'
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <TestStripOverlay 
              isCorrectlyPositioned={isStripPositioned}
              guidanceMessage={guidanceMessage}
            />
            <Stack 
              direction={{xs: "row"}}
              spacing={2} 
              justifyContent="center" 
              sx={{
                position: 'absolute',
                bottom: '4%',
                left: 0,
                right: 0,
              px: 3,
                pt: 3,
                pb: 3,
                zIndex: 20,
                backgroundColor: 'transparent',
                '& button': {
                  boxShadow: 2,
                }
              }}
            >
              <OutlinedButton
                fullWidth
                size="large"
                onClick={() => {
                  stopCamera();
                  setCurrentState('options');
                }}
                sx={{
                  py: 1.5,
                  px: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #D0D5DD',
                  color: 'primary.dark',
                  maxWidth: 200,
                }}
              >
                Cancel
              </OutlinedButton>
              <ContainedButton
                fullWidth
                size="large"
                onClick={takePhoto}
                disabled={!isStripPositioned}
                sx={{
                  py: 1.5,
                  px: 4,
                  maxWidth: 200,
                  opacity: isStripPositioned ? 1 : 0.6,
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(25, 118, 210, 0.5)',
                    color: 'white'
                  }
                }}
              >
                Take Photo
              </ContainedButton>
            </Stack>
          </Box>
        )

      case 'upload':
        return (
          <Box sx={{ width: '100%', maxWidth: 486, height: 300, display: 'flex', flexDirection: 'column' }}>
            {capturedImage ? (
              <Box
                component="img"
                src={capturedImage}
                alt="Test strip"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                }}
              />
            ) : (
              <UploadDropzone onDrop={handleImageDropWithSizeCheck} onBrowseClick={() => {}} />
            )}
            <Stack direction={{xs: "column", md:"row"}} spacing={2} justifyContent="center" mt={3}>
              <OutlinedButton
                fullWidth
                size="large"
                onClick={resetUpload}
                sx={{
                  py: 1.5,
                  px: 4,
                  backgroundColor: 'white',
                  border: '1px solid #D0D5DD',
                  color: 'primary.dark',
                }}
              >
                Retake Picture
              </OutlinedButton>

              <ContainedButton
                fullWidth
                size="large"
                onClick={handleAnalyze}
                disabled={!capturedImage}
                sx={{
                  py: 1.5,
                  px: 4,
                }}
              >
                Analyze Result
              </ContainedButton>
            </Stack>
          </Box>
        )

      case 'analysis':
        return (
          <Box
            sx={{
              width: '100%',
              maxWidth: 500,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={"7rem"} sx={{ color: theme.palette.primary.main }} />
          </Box>
        )
    }
  }
  if (!isVerified) {
    return null;
  }
  return (
    <>
      <CameraInstructionsDialog
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
        onProceed={() => {
          setShowInstructions(false);
          startCamera();
        }}
      />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: 'calc(100vh - 68px)',
          py: 3,
        }}
      >
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={2}>
            <Stack alignItems="center">
              <Typography
                fontSize={{xs: "2rem", md:'2.2rem'}}
                fontWeight={theme.typography.fontWeightBold}
                color='text.primary'
                textAlign={'center'}
              >
                {isAnalyzing ? 'Analyzing your sample' : 'Capture Test Result'}
              </Typography>
              {isAnalyzing && (
                <Typography fontSize={'1rem'} color={theme.palette.grey[600]}>
                  This might take few seconds
                </Typography>
              )}
            </Stack>
            {renderContent()}
          </Stack>
        </Container>
      </Box>
    </>
  )
}
